import { getValueByPath, setValueByPath } from '@modules/themes/mappers/objectPath';
import type { VisualDesignAnalysis } from '@modules/shared-types';
import type { ThemeV2ProjectionPatch } from './projectVisualAnalysisToThemeV2';

export interface NormalizeThemeV2LayoutSettingsResult {
  patch: ThemeV2ProjectionPatch;
  warnings: string[];
}

export interface NormalizeThemeV2LayoutSettingsOptions {
  analysis?: VisualDesignAnalysis;
}

const MIN_TOUCH_TARGET_HEIGHT = 44;
const CANONICAL_PHONE_WIDTH = 390;
const CANONICAL_PHONE_HEIGHT = 844;
const MIN_CROP_SCALE = 1.15;
const MAX_CROP_SCALE = 3;
const SPACING_BUCKETS = [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64];

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const midpoint = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[midpoint];
  return (sorted[midpoint - 1] + sorted[midpoint]) / 2;
}

function normalizeSpacing(value: unknown): number | undefined {
  if (!isFiniteNumber(value)) return undefined;
  if (value > SPACING_BUCKETS[SPACING_BUCKETS.length - 1]) {
    return round(value);
  }
  return SPACING_BUCKETS.find((bucket) => bucket >= value) ?? SPACING_BUCKETS[SPACING_BUCKETS.length - 1];
}

function getCropScale(analysis: VisualDesignAnalysis | undefined): {
  x: number;
  y: number;
  average: number;
  medianWidth?: number;
  medianHeight?: number;
} {
  const rects = analysis?.screenInventory
    ?.map((item) => item.cropRect)
    .filter((rect): rect is NonNullable<typeof rect> => (
      Boolean(rect) &&
      isFiniteNumber(rect?.width) &&
      isFiniteNumber(rect?.height) &&
      rect.width > 0 &&
      rect.height > 0
    )) ?? [];

  const medianWidth = median(rects.map((rect) => rect.width));
  const medianHeight = median(rects.map((rect) => rect.height));
  if (!medianWidth || !medianHeight) {
    return { x: 1, y: 1, average: 1 };
  }

  const x = clamp(CANONICAL_PHONE_WIDTH / medianWidth, 1, MAX_CROP_SCALE);
  const y = clamp(CANONICAL_PHONE_HEIGHT / medianHeight, 1, MAX_CROP_SCALE);
  return {
    x,
    y,
    average: (x + y) / 2,
    medianWidth,
    medianHeight,
  };
}

function writeIfChanged(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string,
  nextValue: unknown,
  reason: string
): void {
  const currentValue = getValueByPath(patch, path);
  if (currentValue === undefined || JSON.stringify(currentValue) === JSON.stringify(nextValue)) {
    return;
  }

  setValueByPath(patch as Record<string, unknown>, path, nextValue);
  warnings.push(`Normalized ${path} from ${JSON.stringify(currentValue)} to ${JSON.stringify(nextValue)} (${reason}).`);
}

function scaleNumberPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string,
  scale: number,
  options: {
    min?: number;
    max?: number;
    onlyBelow?: number;
    reason: string;
  }
): void {
  if (scale < MIN_CROP_SCALE) return;

  const value = getValueByPath(patch, path);
  if (!isFiniteNumber(value)) return;
  if (options.onlyBelow !== undefined && value >= options.onlyBelow) return;

  const min = options.min ?? Number.NEGATIVE_INFINITY;
  const max = options.max ?? Number.POSITIVE_INFINITY;
  const scaled = round(clamp(value * scale, min, max));
  writeIfChanged(patch, warnings, path, scaled, options.reason);
}

function scaleNumberMapPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string,
  scale: number,
  options: {
    min: number;
    max: number;
    reason: string;
  }
): void {
  if (scale < MIN_CROP_SCALE) return;

  const value = getValueByPath(patch, path);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;

  const nextValue: Record<string, unknown> = {};
  let changed = false;
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (!isFiniteNumber(child) || child === 0) {
      nextValue[key] = child;
      continue;
    }

    const scaled = round(clamp(child * scale, options.min, options.max));
    nextValue[key] = scaled;
    changed = changed || scaled !== child;
  }

  if (changed) {
    writeIfChanged(patch, warnings, path, nextValue, options.reason);
  }
}

function scaleInsetsPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string,
  scaleX: number,
  scaleY: number,
  reason: string
): void {
  if (Math.max(scaleX, scaleY) < MIN_CROP_SCALE) return;

  const value = getValueByPath(patch, path);
  if (!value || typeof value !== 'object') return;

  const normalized = { ...(value as Record<string, unknown>) };
  let changed = false;
  const scales = {
    top: scaleY,
    bottom: scaleY,
    start: scaleX,
    end: scaleX,
  };

  for (const side of ['top', 'bottom', 'start', 'end'] as const) {
    const sideValue = normalized[side];
    if (!isFiniteNumber(sideValue) || sideValue === 0) continue;
    const scaled = round(clamp(sideValue * scales[side], 0, 72));
    normalized[side] = scaled;
    changed = changed || scaled !== sideValue;
  }

  if (changed) {
    writeIfChanged(patch, warnings, path, normalized, reason);
  }
}

function normalizeHeightPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string
): void {
  const value = getValueByPath(patch, path);
  if (!isFiniteNumber(value) || value >= MIN_TOUCH_TARGET_HEIGHT) return;
  writeIfChanged(patch, warnings, path, MIN_TOUCH_TARGET_HEIGHT, 'minimum mobile touch target');
}

function normalizeSpacingPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string
): void {
  const value = getValueByPath(patch, path);
  const normalized = normalizeSpacing(value);
  if (normalized === undefined) return;
  writeIfChanged(patch, warnings, path, normalized, 'Theme V2 spacing bucket');
}

function normalizeInsetsPath(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  path: string
): void {
  const value = getValueByPath(patch, path);
  if (!value || typeof value !== 'object') return;

  const normalized = { ...(value as Record<string, unknown>) };
  let changed = false;
  for (const side of ['top', 'bottom', 'start', 'end'] as const) {
    const sideValue = normalized[side];
    const sideNormalized = normalizeSpacing(sideValue);
    if (sideNormalized !== undefined && sideNormalized !== sideValue) {
      normalized[side] = sideNormalized;
      changed = true;
    }
  }

  if (changed) {
    writeIfChanged(patch, warnings, path, normalized, 'Theme V2 spacing bucket');
  }
}

function normalizeCropScaledValues(
  patch: ThemeV2ProjectionPatch,
  warnings: string[],
  analysis: VisualDesignAnalysis | undefined
): void {
  const scale = getCropScale(analysis);
  if (scale.average < MIN_CROP_SCALE) return;

  const reason = `canonical phone scale from detected ${round(scale.medianWidth ?? 0)}x${round(scale.medianHeight ?? 0)} screen crops`;

  const typographyPaths = [
    { path: 'fonts.title.fontSize', min: 18, max: 40, onlyBelow: 16 },
    { path: 'fonts.subtitle.fontSize', min: 12, max: 24, onlyBelow: 12 },
    { path: 'optionsListSettings.itemTitle.fontSize', min: 12, max: 24, onlyBelow: 12 },
    { path: 'optionsListSettings.itemSubtitle.fontSize', min: 11, max: 20, onlyBelow: 11 },
  ];
  typographyPaths.forEach((entry) => scaleNumberPath(patch, warnings, entry.path, scale.y, {
    min: entry.min,
    max: entry.max,
    onlyBelow: entry.onlyBelow,
    reason,
  }));

  const heightPaths = [
    { path: 'layoutSettings.actionBar.primaryButton.height', min: MIN_TOUCH_TARGET_HEIGHT, max: 64, onlyBelow: MIN_TOUCH_TARGET_HEIGHT },
    { path: 'layoutSettings.actionBar.secondaryButton.height', min: MIN_TOUCH_TARGET_HEIGHT, max: 64, onlyBelow: MIN_TOUCH_TARGET_HEIGHT },
    { path: 'layoutSettings.form.fieldHeight', min: MIN_TOUCH_TARGET_HEIGHT, max: 64, onlyBelow: MIN_TOUCH_TARGET_HEIGHT },
    { path: 'actionBar.primary.appearance.dimensions.height', min: MIN_TOUCH_TARGET_HEIGHT, max: 64, onlyBelow: MIN_TOUCH_TARGET_HEIGHT },
    { path: 'actionBar.secondary.appearance.dimensions.height', min: MIN_TOUCH_TARGET_HEIGHT, max: 64, onlyBelow: MIN_TOUCH_TARGET_HEIGHT },
    { path: 'layoutSettings.progress.indicatorHeight', min: 4, max: 12, onlyBelow: 4 },
    { path: 'layoutSettings.progress.trackHeight', min: 4, max: 12, onlyBelow: 4 },
  ];
  heightPaths.forEach((entry) => scaleNumberPath(patch, warnings, entry.path, scale.y, {
    min: entry.min,
    max: entry.max,
    onlyBelow: entry.onlyBelow,
    reason,
  }));

  const radiusPaths = [
    'layoutSettings.actionBar.primaryButton.radius',
    'layoutSettings.actionBar.secondaryButton.radius',
    'layoutSettings.optionsList.itemRadius',
    'layoutSettings.form.fieldRadius',
    'layoutSettings.picker.containerRadius',
    'layoutSettings.paywall.productRadius',
    'layoutSettings.flexibleLayout.sectionRadius',
    'layoutSettings.media.cornerRadius',
    'actionBar.primary.appearance.borders.radius.uniform',
    'actionBar.secondary.appearance.borders.radius.uniform',
  ];
  radiusPaths.forEach((path) => scaleNumberPath(patch, warnings, path, scale.average, {
    min: 2,
    max: 32,
    onlyBelow: 14,
    reason,
  }));

  const spacingPaths = [
    'layoutSettings.actionBar.buttonGap',
    'layoutSettings.optionsList.itemsSpacing',
    'layoutSettings.paywall.productSpacing',
    'layoutSettings.flexibleLayout.sectionSpacing',
  ];
  spacingPaths.forEach((path) => scaleNumberPath(patch, warnings, path, scale.average, {
    min: 0,
    max: 64,
    onlyBelow: 24,
    reason,
  }));

  scaleNumberMapPath(patch, warnings, 'layoutSettings.spacingScale', scale.average, {
    min: 0,
    max: 64,
    reason,
  });
  scaleNumberMapPath(patch, warnings, 'layoutSettings.radiusScale', scale.average, {
    min: 0,
    max: 48,
    reason,
  });

  const insetPaths = [
    'layoutSettings.actionBar.insets',
    'layoutSettings.actionBar.primaryButton.insets',
    'layoutSettings.actionBar.secondaryButton.insets',
    'layoutSettings.optionsList.itemInsets',
    'layoutSettings.optionsList.layoutInsets',
    'layoutSettings.form.fieldInsets',
    'layoutSettings.picker.containerInsets',
    'layoutSettings.progress.containerInsets',
    'layoutSettings.paywall.productInsets',
    'layoutSettings.paywall.footerInsets',
    'layoutSettings.webEmbed.contentInsets',
    'layoutSettings.flexibleLayout.containerInsets',
    'layoutSettings.flexibleLayout.sectionPadding',
    'actionBar.appearance.insets',
    'actionBar.primary.appearance.insets',
    'actionBar.secondary.appearance.insets',
  ];
  insetPaths.forEach((path) => scaleInsetsPath(patch, warnings, path, scale.x, scale.y, reason));
}

/**
 * Normalize image-derived geometry before it becomes a user-facing Theme V2 draft.
 *
 * The raw projection remains useful for extraction evals. Drafts need a second,
 * product-oriented pass so sub-44px buttons and fractional spacing from DOM
 * captures do not create hard-to-use themes.
 */
export function normalizeThemeV2LayoutSettingsForDraft(
  projectionPatch: ThemeV2ProjectionPatch,
  options: NormalizeThemeV2LayoutSettingsOptions = {}
): NormalizeThemeV2LayoutSettingsResult {
  const patch = cloneJson(projectionPatch);
  const warnings: string[] = [];

  normalizeCropScaledValues(patch, warnings, options.analysis);

  const heightPaths = [
    'layoutSettings.actionBar.primaryButton.height',
    'layoutSettings.actionBar.secondaryButton.height',
    'layoutSettings.form.fieldHeight',
    'actionBar.primary.appearance.dimensions.height',
    'actionBar.secondary.appearance.dimensions.height',
  ];
  heightPaths.forEach((path) => normalizeHeightPath(patch, warnings, path));

  const spacingPaths = [
    'layoutSettings.actionBar.buttonGap',
    'layoutSettings.optionsList.itemsSpacing',
    'layoutSettings.paywall.productSpacing',
    'layoutSettings.flexibleLayout.sectionSpacing',
  ];
  spacingPaths.forEach((path) => normalizeSpacingPath(patch, warnings, path));

  const insetPaths = [
    'layoutSettings.actionBar.insets',
    'layoutSettings.actionBar.primaryButton.insets',
    'layoutSettings.actionBar.secondaryButton.insets',
    'layoutSettings.optionsList.itemInsets',
    'layoutSettings.optionsList.layoutInsets',
    'layoutSettings.form.fieldInsets',
    'layoutSettings.picker.containerInsets',
    'layoutSettings.progress.containerInsets',
    'layoutSettings.paywall.productInsets',
    'layoutSettings.paywall.footerInsets',
    'layoutSettings.webEmbed.contentInsets',
    'layoutSettings.flexibleLayout.containerInsets',
    'layoutSettings.flexibleLayout.sectionPadding',
    'actionBar.appearance.insets',
    'actionBar.primary.appearance.insets',
    'actionBar.secondary.appearance.insets',
  ];
  insetPaths.forEach((path) => normalizeInsetsPath(patch, warnings, path));

  return { patch, warnings };
}
